import { put, call, takeLatest } from 'redux-saga/effects';
import * as R from 'ramda';
import { requestWithAuth, processErr } from '../api/request';
import * as actions from '../actions/admin';
import {
    LOGOUT_REQUEST,
    GET_ADMIN_LIST_REQUEST,
    GET_ADMIN_ROLE_LIST_REQUEST,
    ADD_ADMIN_REQUEST,
    EDIT_ADMIN_REQUEST,
    DELETE_ADMIN_REQUEST,
    REFRESH_TOKEN_REQUEST,
    CHANGE_PASSWORD_REQUEST
} from '../constant/actionTypes';

const url = '/admin';
const pageSize = 20;

const refreshTokenAPI = payload => {
    return requestWithAuth().put(`${url}/refreshToken`, payload)
        .then(res => {
            if (res.status === 200) {
                sessionStorage.setItem('t', res.data.sessionToken);
                sessionStorage.setItem('r', res.data.refreshToken);
                return {
                    status: 'success'
                };
            }
            throw Error('Token refreshing failed!');
        }).catch(err => { throw processErr(err); });
};

const changePasswordAPI = payload => {
    return requestWithAuth().post(`/user/password`, payload)
        .then(res => ({
            ...res.data,
            status: 'success'
        })).catch(err => { throw processErr(err); });
}

/*
 * @description
 * payload format :
 * - page: Number
 * - search: String
 */
const getAdminListAPI = payload => {
    const {
        page,
        search
    } = payload
    let endpoint = `${url}/search?size=${pageSize}`;
    if (!R.isNil(page)) {
        endpoint += `&page=${page - 1}`;
    }
    if (!R.isNil(search)) {
        endpoint += `&email=${search}`;
    }
    return requestWithAuth().get(endpoint)
        .then(res => ({
            data: res.data.list,
            pagination: {
                page: R.isNil(page) ? undefined : page,
                pages: Math.ceil(res.data.size / pageSize),
                total: res.data.size,
            }
        })).catch(err => { throw processErr(err); })
}

const getAdminRoleListAPI = () => {
    return requestWithAuth().get(`${url}/roles`)
        .then(res => {
            const result = res.data.map(role => {
                switch (role.id) {
                    case 0: // superadmin
                        return {
                            ...role,
                            permission: {
                                "entrance": ["read"],
                                "admins": ["read", "create", "update", "delete"],
                                "banners": ["read", "create", "update", "delete"],
                                "news": ["read", "create", "update", "delete"],
                                "games": ["read", "create", "update", "delete"],
                                "users": ["read", "create", "update", "delete"],
                                "transactions": ["read", "create", "update", "delete"],
                            }
                        };
                    case 1: // admin
                        return {
                            ...role,
                            permission: {
                                "entrance": ["read"],
                                "admins": ["read"],
                                "banners": ["read", "create", "update"],
                                "news": ["read", "create", "update", "delete"],
                                "games": ["read", "create", "update", "delete"],
                                "users": ["read", "create", "update", "delete"],
                                "transactions": ["read", "create", "update", "delete"],
                            }
                        };
                    case 2: // marketing
                        return {
                            ...role,
                            permission: {
                                "entrance": ["read"],
                                "admins": [],
                                "banners": ["read", "create", "update"],
                                "news": ["read", "create", "update", "delete"],
                                "games": ["read"],
                                "users": ["read"],
                                "transactions": ["read"],
                            }
                        };
                    default:
                        return {
                            ...role,
                            permission: {}
                        };
                }
            });
            return result;
        }).catch(err => { throw processErr(err); })
};

const addAdminAPI = payload => {
    return requestWithAuth().post(`${url}/create`, payload)
        .then(res => ({
            ...res.data,
            status: 'success'
        })).catch(err => { throw processErr(err); })
};

const editAdminAPI = payload => {

    return requestWithAuth().post(`${url}/update?email=${payload.email}`, payload.data)
        .then(res => ({
            ...res.data,
            status: 'success'
        })).catch(err => { throw processErr(err); })
}

const deleteAdminAPI = payload => {

    return requestWithAuth().delete(`${url}/target?email=${payload.email}`)
        .then(res => ({
            ...res.data,
            status: 'success'
        })).catch(err => { throw processErr(err); })
}

function* logout() {
    sessionStorage.removeItem('t');
    sessionStorage.removeItem('u');
    sessionStorage.removeItem('r');
    yield put(actions.logoutSuccess());
}

function* refreshToken(action) {
    try {
        const result = yield call(refreshTokenAPI, action.payload);
        yield put(actions.refreshTokenSuccess(result));
    } catch (e) {
        yield put(actions.refreshTokenFailure(e));
    }
}

function* changePassword(action) {
    try {
        const result = yield call(changePasswordAPI, action.payload);
        yield put(actions.changePasswordSuccess(result));
    } catch (e) {
        yield put(actions.changePasswordFailure(e));
    }
}

function* getAdminList(action) {
    try {
        const adminList = yield call(getAdminListAPI, action.payload);
        yield put(actions.getAdminListSuccess(adminList));
    } catch (e) {
        yield put(actions.getAdminListFailure(e));
    }
}

function* getRoleList() {
    try {
        const roleList = yield call(getAdminRoleListAPI);
        yield put(actions.getAdminRoleListSuccess(roleList));
    } catch (e) {
        yield put(actions.getAdminRoleListFailure(e));
    }
}

function* addAdmin(action) {
    try {
        const admin = yield call(addAdminAPI, action.payload);
        yield put(actions.addAdminSuccess(admin));
        yield getAdminList({ payload: { page: 1 } });
    } catch (e) {
        yield put(actions.addAdminFailure(e));
    }
}

function* editAdmin(action) {
    try {
        const admin = yield call(editAdminAPI, action.payload);
        yield put(actions.editAdminSuccess(admin));
        yield getAdminList({ payload: { page: 1 } });
    } catch (e) {
        yield put(actions.editAdminFailure(e));
    }
}

function* deleteAdmin(action) {
    try {
        const result = yield call(deleteAdminAPI, action.payload);
        yield put(actions.deleteAdminSuccess(result));
        yield getAdminList({ payload: { page: 1 } });
    } catch (e) {
        yield put(actions.deleteAdminFailure(e));
    }
}

const admin = [
    takeLatest(LOGOUT_REQUEST, logout),
    takeLatest(REFRESH_TOKEN_REQUEST, refreshToken),
    takeLatest(CHANGE_PASSWORD_REQUEST, changePassword),
    takeLatest(GET_ADMIN_LIST_REQUEST, getAdminList),
    takeLatest(GET_ADMIN_ROLE_LIST_REQUEST, getRoleList),
    takeLatest(ADD_ADMIN_REQUEST, addAdmin),
    takeLatest(EDIT_ADMIN_REQUEST, editAdmin),
    takeLatest(DELETE_ADMIN_REQUEST, deleteAdmin),
];

export default admin;
